import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/Button';
import { ArrowRightIcon, ShieldCheckIcon, ZapIcon, TrendingUpIcon } from 'lucide-react';
import { OPENFINANCE_THEME, getOpenFinanceLogo } from '../../constants/openFinanceTheme';
export interface PFMBeforeScreenProps {
  onConnectBank: () => void;
}
export function PFMBeforeScreen({
  onConnectBank
}: PFMBeforeScreenProps) {
  return <div className="space-y-6" data-id="element-1161">
      {/* Logo */}
      <motion.div initial={{
      scale: 0.8,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="flex justify-center" data-id="element-1162">
        <div className="p-4 rounded-full" style={{
        background: OPENFINANCE_THEME.gradients.card,
        boxShadow: OPENFINANCE_THEME.shadows.lg
      }} data-id="element-1163">
          <img src={getOpenFinanceLogo('xl')} alt="OpenFinance-OS" className="w-24 h-24" data-id="element-1164" />
        </div>
      </motion.div>

      {/* Heading with Fraunces font */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: 0.2
    }} className="text-center" data-id="element-1165">
        <h1 className="text-3xl font-bold mb-3 leading-tight" style={{
        fontFamily: OPENFINANCE_THEME.fonts.display,
        color: OPENFINANCE_THEME.colors.primary700
      }} data-id="element-1166">
          Welcome to VeloX
        </h1>
        <p className="text-base leading-relaxed" style={{
        color: OPENFINANCE_THEME.colors.textLight,
        fontFamily: OPENFINANCE_THEME.fonts.body
      }} data-id="element-1167">
          Connect your bank accounts to unlock powerful financial insights and
          management tools.
        </p>
      </motion.div>

      {/* Feature Cards */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: 0.3
    }} className="space-y-3" data-id="element-1168">
        {[{
        icon: TrendingUpIcon,
        title: 'Track Your Spending',
        description: 'Automatic categorization and insights'
      }, {
        icon: ZapIcon,
        title: 'Real-Time Updates',
        description: 'Always know your current balance'
      }, {
        icon: ShieldCheckIcon,
        title: 'Bank-Level Security',
        description: 'Protected by UAE Open Finance standards'
      }].map((feature, index) => <motion.div key={feature.title} initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.4,
        delay: 0.4 + index * 0.1
      }} className="flex items-start gap-4 p-4" style={{
        backgroundColor: OPENFINANCE_THEME.colors.cardBg,
        border: `2px solid ${OPENFINANCE_THEME.colors.borderLight}`,
        borderRadius: OPENFINANCE_THEME.radius.lg
      }} data-id="element-1169">
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{
          background: OPENFINANCE_THEME.gradients.card,
          borderRadius: OPENFINANCE_THEME.radius.md
        }} data-id="element-1170">
              <feature.icon className="w-5 h-5" style={{
            color: OPENFINANCE_THEME.colors.primary500
          }} data-id="element-1171" />
            </div>
            <div data-id="element-1172">
              <div className="font-semibold text-sm mb-1" style={{
            color: OPENFINANCE_THEME.colors.text,
            fontFamily: OPENFINANCE_THEME.fonts.body
          }} data-id="element-1173">
                {feature.title}
              </div>
              <div className="text-xs leading-relaxed" style={{
            color: OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-1174">
                {feature.description}
              </div>
            </div>
          </motion.div>)}
      </motion.div>

      {/* CTA Button with amber accent */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: 0.7
    }} className="pt-2" data-id="element-1175">
        <Button onClick={onConnectBank} variant="accent" className="w-full group" data-id="element-1176">
          <div className="flex items-center justify-center gap-2" data-id="element-1177">
            <span data-id="element-1178">Connect Your Bank</span>
            <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-1179" />
          </div>
        </Button>
      </motion.div>

      {/* Trust Badge */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.5,
      delay: 0.8
    }} className="text-center p-4 rounded-xl" style={{
      backgroundColor: OPENFINANCE_THEME.colors.primary50,
      borderRadius: OPENFINANCE_THEME.radius.lg
    }} data-id="element-1180">
        <div className="flex items-center justify-center gap-2 text-xs font-medium" style={{
        color: OPENFINANCE_THEME.colors.primary700
      }} data-id="element-1181">
          <ShieldCheckIcon className="w-4 h-4" data-id="element-1182" />
          <span data-id="element-1183">Powered by OpenFinance-OS · UAE Open Finance Certified</span>
        </div>
      </motion.div>
    </div>;
}